import autobind from 'autobind-decorator';
import PropTypes from 'prop-types';
import { Component } from 'react';
import Notifications from 'react-notification-system-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { InputSelectProps } from '../../../../common/components/form-elements/InputSelect';
import * as Actions from '../../../../common/services/customer/Actions';
import * as CustomerService from '../../../../common/services/customer/Service';
import { CustomerActionPropTypes, CustomerError } from '../../../../common/services/customer/Types';
import { CardDetailsDTO } from '../../../../common/types/dto/CardDetailsDTO';
import { LookupValueDTO } from '../../../../common/types/dto/LookupValueDTO';
import { ConfigProp, RenderPropWithProps, RouteProps, TranslationFuncProp } from '../../../../common/types/prop-types/common';
import { RootState } from '../../../base/RootReducer';
import { config } from '../../../common/';
import { AppThunkDispatch } from '../../../common/types';

export interface DeleteCardProps extends ConfigProp {
    licensePlateSelect: InputSelectProps;
    selectedCard: CardDetailsDTO | undefined;
    confirmation: boolean;
    submitErrorText: string;
    onConfirmChange: (event: React.ChangeEvent<HTMLInputElement>) => void;

    btnSubmit: {
        onClick: (event: React.MouseEvent<{}> | null, onSuccess?: () => void | null) => void;
        value: string;
        disabled: boolean;
    };
}

export interface DeleteCardContainerProps {
}

interface ReduxStateProps {
    isCommunicating: boolean;
    customerCards: CardDetailsDTO[] | undefined;
}

interface ReduxDispatchProps {
    customerActions: typeof Actions;
    customerServiceActions: CustomerService.ServiceActions;
    dispatch: AppThunkDispatch;
}

interface DeleteCardContainerState {
    licensePlate: string;
    licensePlateValMsg: string;
    selectedCard: CardDetailsDTO | undefined;
    submitErrorText: string;
    confirmation: boolean;
}

class DeleteCardContainer extends Component<
    DeleteCardContainerProps &
    TranslationFuncProp &
    RouteProps &
    RenderPropWithProps<DeleteCardProps> &
    ReduxStateProps &
    ReduxDispatchProps,
    DeleteCardContainerState
    > {
    public static propTypes = {
        t: PropTypes.func.isRequired,
        isCommunicating: PropTypes.bool.isRequired,
        routeProps: PropTypes.any.isRequired,
        customerCards: PropTypes.any,
        customerActions: CustomerActionPropTypes.isRequired,
        customerServiceActions: CustomerService.ServiceActionPropTypes.isRequired,
        dispatch: PropTypes.func.isRequired,
        //
        children: PropTypes.func.isRequired,
    };

    public readonly state: DeleteCardContainerState = {
        licensePlate: '',
        licensePlateValMsg: '',
        submitErrorText: '',
        confirmation: false,
        selectedCard: undefined,
    };

    public async componentDidMount() {
        await this.props.customerServiceActions.customerCards();
    }

    public validateForm() {
        if (this.props.isCommunicating) {
            return false;
        }
        let isValid: boolean = true;
        if (this.state.licensePlate === '' || this.state.licensePlate === undefined) {
            this.setState({ licensePlateValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (!config.Constants.Regex.LicensePlateValidation.test(this.state.licensePlate.toUpperCase())) {
            this.setState({ licensePlateValMsg: this.props.t('signup:errors:licensePlateValidationRegex') });
            isValid = false;
        }
        if (!this.state.confirmation) {
            this.setState({ submitErrorText: this.props.t('mandatory') });
            isValid = false;
        }
        return isValid;
    }

    @autobind
    public onConfirmChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({ confirmation: !this.state.confirmation });
    }

    @autobind
    public async onSubmit(e: React.MouseEvent<{}> | null, onSuccess?: () => void | null) {
        if (e) {
            e.preventDefault();
        }
        if (!this.validateForm()) {
            return;
        }
        try {
            await this.props.customerServiceActions.deleteCard(this.state.licensePlate);
            await this.props.customerServiceActions.customerData();
            this.props.dispatch(Notifications.success({
                title: this.props.t('success'),
                message: this.props.t('cards:deleteCard.success'),
                position: 'tc',
                autoDismiss: 5,
            }));
            await this.props.customerServiceActions.customerData();
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            if (error instanceof CustomerError) {
                const exceptionType: string = error.customerErrorInfo.exceptionType || 'UnknownException';
                const exceptionErrorMsg: string = this.props.t(`exception:${exceptionType.split('.').pop()}`);
                this.setState({ submitErrorText: exceptionErrorMsg });
                this.props.dispatch(Notifications.error({
                    title: this.props.t('error'),
                    message: exceptionErrorMsg,
                    position: 'tc',
                    autoDismiss: 5,
                }));
            } else {
                throw error;
            }
        }
    }

    @autobind
    public onLicensePlateChange(event: any) {
        if (this.props.customerCards) {
            this.setState({
                licensePlate: event.value,
                selectedCard: this.props.customerCards.find((x: CardDetailsDTO) => x.licensePlate === event.value),
            });
        }
    }

    public render() {
        const licensePlateArray: Array<LookupValueDTO<string>> = [];
        if (this.props.customerCards) {
            this.props.customerCards.map((card) => {
                if (card.licensePlate) {
                    licensePlateArray.push({
                        label: card.licensePlate,
                        value: card.licensePlate,
                    });
                }
            });
        }

        const newProps: DeleteCardProps = {
            licensePlateSelect: {
                inputProps: {
                    value: this.state.licensePlate,
                },
                onChange: this.onLicensePlateChange,
                errorText: this.state.licensePlateValMsg,
                hintText: '',
                options: licensePlateArray,
                placeholder: licensePlateArray.length > 0 ? this.props.t('cards:newCard.selectPlate') : this.props.t('loadingText'),
            },
            selectedCard: this.state.selectedCard,
            confirmation: this.state.confirmation,
            onConfirmChange: this.onConfirmChange,
            submitErrorText: this.state.submitErrorText,
            btnSubmit: {
                onClick: this.onSubmit,
                value: this.props.t('cards:deleteCard.request'),
                disabled: this.props.isCommunicating,
            },
            config,
        };
        return this.props.children(newProps);
    }
}

const mapStateToProps = (store: RootState) => ({
    isCommunicating: store.customerState.deleteCard.isCommunicating,
    customerCards: store.customerState.customerCards.data,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    customerActions: bindActionCreators(Actions, dispatch),
    customerServiceActions: CustomerService.bindServiceActionCreators(config, dispatch),
    dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(DeleteCardContainer);
